import { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as React from 'react';

// project imports
import FormControl from 'components/forms/Form/FormControl';
import { selectEaiEtagList } from 'features/eai/selectors';
import { useAppDispatch, useAppSelector } from 'hooks/useAppStore';
import {
  selectDeliveryPointDetail,
  selectDeliveryPointGDSList
} from 'features/eai/selectors/deliveryPointSelectors';
import {
  getDeliveryPointGDSList,
  unassignGDSFromDeliveryPoint
} from 'features/eai/thunks/deliveryPointThunks';
import CreateGDSDialog from 'components/dialog/CreateGDSDialog';
import ContextActionGuard from 'utils/guards/ContextActionGuard';
import DenseTable from 'components/tables/TableDense';
import {
  setDeliveryPointGDSList,
  setShowGdsDetail
} from 'features/eai/eaiSlice';
import { createFormControlProps, createValidationSchema } from 'utils';
import useTenant from 'hooks/useTenant';

// material-ui
import { Block } from '@mui/icons-material';
import { Button, Grid, Stack } from '@mui/material';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';

// assets
import { IconPlus } from '@tabler/icons';

// types
import { FormInput } from 'models/form';
import { HeadCell, FilterCategory, TableAction } from 'models/table';
import { ArrangementOrder, ValidityType } from 'models';
import {
  AppModule,
  ContextActions,
  DateFormat,
  EAIEntityTypeCode,
  GRID_SPACING,
  InputType,
  MAXIMAL_VALID_TO_DATE,
  Routes,
  YupType
} from 'utils/constants';
import { GDSInterface } from 'features/eai/eaiTypes';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog/ConfirmationDialog';
import Filter from 'components/assets/Filter';
import { FilterStateType } from 'components/assets/FilterContent';
import { selectAjaxLoader } from 'features/app/appSelectors';
import { replaceDynamicPathParams } from 'routes';

interface GDSRow {
  id: string;
  name: string;
  validFor: ValidityType;
}

// constants
const I18N_COMPONENT_ROOT =
  'module.eai.deliveryPoints.deliveryPointDetail.gdsList';
const I18N_MODAL_ROOT = 'dialogs.invalidateGDS';

const { CA_EAI_EDIT_DELIVERY_POINT_GDS, CA_EAI_LIST_DELIVERY_POINT_GDS } =
  ContextActions.EAI;

const VALID_TO_DATE_GT_FOR_SHOWING_ALL_GDS = '0000-01-01T00:00:00.000000+02:00';

const INITIAL_VALID_SORT = 'validFromDate,asc';

// ==============================|| DELIVERY POINT GDS LIST ||============================== //

const GDSList = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenant } = useTenant();

  const [tableData, setTableData] = useState<GDSRow[]>([]);

  const deliveryPointDetail = useAppSelector(selectDeliveryPointDetail);
  const gdsList = useAppSelector(selectDeliveryPointGDSList);
  const operationInProgress = useAppSelector(selectAjaxLoader);

  const [searchString, setSearchString] = React.useState('');
  const [validFromDateLt, setValidFromDateLt] = React.useState('');
  const [validToDateGt, setValidToDateGt] = React.useState('');

  const [confirmationDialogParameter, setConfirmationDialogParameter] =
    useState<string | undefined>(undefined);

  const etagList = useAppSelector(selectEaiEtagList);

  const filterStructure: FilterCategory[] = [
    {
      name: 'showOnlyValid',
      label: t(`${I18N_COMPONENT_ROOT}.filter.validity`),
      type: 'checkbox',
      filterCategoryItems: [
        {
          value: 'showOnlyValid',
          label: t(`${I18N_COMPONENT_ROOT}.filter.showOnlyValid`)
        }
      ]
    }
  ];

  const [sort, setSort] = React.useState(INITIAL_VALID_SORT);
  const [order, setOrder] = React.useState<ArrangementOrder>('asc');
  const [orderBy, setOrderBy] = React.useState('validFromDate');

  const defaultFilterState: FilterStateType = {
    showOnlyValid: []
  };

  const [filterState, setFilterState] =
    useState<FilterStateType>(defaultFilterState);

  const [isCreateGDSDialogOpen, setIsCreateGDSDialogOpen] =
    useState<boolean>(false);

  useEffect(
    () => {
      if (deliveryPointDetail?.id) {
        dispatch(
          getDeliveryPointGDSList(
            deliveryPointDetail.id,
            '',
            '',
            VALID_TO_DATE_GT_FOR_SHOWING_ALL_GDS,
            sort
          )
        );
      }
      return () => {
        dispatch(setDeliveryPointGDSList([]));
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveryPointDetail]
  );

  useEffect(
    () => {
      if (gdsList.length > 0) {
        setTableData(
          gdsList.map((gds: GDSInterface) => {
            return {
              id: gds.id,
              name: gds.name,
              validFor: gds.validity,
              actions: generateDeliveryPointActions(
                gds.id,
                gds.validity.validToDate === MAXIMAL_VALID_TO_DATE
              )
            };
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gdsList]
  );

  const clearFilterAndSearchAndDispatchGetGDSList = () => {
    setSearchString('');
    setSort(INITIAL_VALID_SORT);
    setValidFromDateLt('');
    setValidToDateGt(VALID_TO_DATE_GT_FOR_SHOWING_ALL_GDS);

    if (deliveryPointDetail?.id) {
      dispatch(
        getDeliveryPointGDSList(
          deliveryPointDetail.id,
          '',
          '',
          VALID_TO_DATE_GT_FOR_SHOWING_ALL_GDS,
          sort
        )
      );
    }
  };

  const handleFilterChange = (newFilterState: FilterStateType | undefined) => {
    const filterToParse = newFilterState || defaultFilterState;
    setFilterState(filterToParse);

    let validFromDateLtToUse = '';
    let validToDateGtToUse = VALID_TO_DATE_GT_FOR_SHOWING_ALL_GDS;

    if (filterToParse['showOnlyValid']?.length === 1) {
      const now = moment(new Date()).format(DateFormat.REST_DATETIME_FORMAT);
      setValidFromDateLt(now);
      setValidToDateGt(now);
      validFromDateLtToUse = now;
      validToDateGtToUse = now;
    }

    if (deliveryPointDetail) {
      dispatch(
        getDeliveryPointGDSList(
          deliveryPointDetail.id,
          searchString,
          validFromDateLtToUse,
          validToDateGtToUse,
          sort
        )
      );
    }
  };

  const handleRequestSort = (
    _event: React.SyntheticEvent,
    property: string
  ) => {
    if (!deliveryPointDetail) {
      return;
    }

    if (property === 'validity') {
      property = 'validFromDate';
    }

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setSort(property + ',' + order);

    dispatch(
      getDeliveryPointGDSList(
        deliveryPointDetail.id,
        searchString,
        validFromDateLt,
        validToDateGt,
        property + ',' + order
      )
    );
  };

  const onSearchChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchString(event.target.value);
    if (deliveryPointDetail) {
      dispatch(
        getDeliveryPointGDSList(
          deliveryPointDetail.id,
          event.target.value,
          validFromDateLt,
          validToDateGt,
          sort
        )
      );
    }
  };

  const removeGDSFromDeliveryPoint = () => {
    if (deliveryPointDetail?.id && confirmationDialogParameter) {
      dispatch(
        unassignGDSFromDeliveryPoint(
          deliveryPointDetail.id,
          confirmationDialogParameter,
          etagList.deliveryPoint,
          () => {
            setConfirmationDialogParameter(undefined);
            clearFilterAndSearchAndDispatchGetGDSList();
          }
        )
      );
    }
  };

  const generateDeliveryPointActions = (
    parameter: string,
    isValid: boolean
  ): TableAction[] => {
    const tableActionsToReturn: TableAction[] = [];

    if (isValid) {
      tableActionsToReturn.push({
        type: 'invalidate',
        icon: <Block />,
        variant: 'contained',
        title: t(`common.table.unassign`).toString(),
        link: '',
        onClick: () => {
          setConfirmationDialogParameter(parameter);
        }
      });
    }

    tableActionsToReturn.push({
      type: 'detail',
      icon: <VisibilityTwoToneIcon sx={{ fontSize: '1.3rem' }} />,
      link: '',
      title: 'Detail',
      onClick: () => {
        dispatch(setShowGdsDetail(true));
        navigate(
          replaceDynamicPathParams(
            Routes.EAI.DELIVERY_POINTS_DETAIL_PATH_WITH_GDS_ID,
            [
              {
                paramName: 'tenant',
                value: tenant
              },
              {
                paramName: 'id',
                value: deliveryPointDetail?.id
              },
              {
                paramName: 'gdsId',
                value: parameter
              }
            ]
          )
        );
      }
    });

    return tableActionsToReturn;
  };

  const headCells: HeadCell[] = [
    {
      id: 'id',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.id`),
      align: 'left',
      sortable: true
    },
    {
      id: 'name',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.name`),
      align: 'right',
      sortable: true
    },
    {
      id: 'validity',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.validity`),
      align: 'right',
      sortable: true
    },
    {
      id: 'actions',
      numeric: false,
      disablePadding: false,
      label: t(`${I18N_COMPONENT_ROOT}.columns.actions`),
      align: 'right',
      sortable: false
    }
  ];

  const getInputs = (): FormInput[] => [
    {
      id: 'idOrName',
      validation: {
        type: YupType.STRING,
        methods: [{ methodType: 'trim' }]
      }
    }
  ];

  // const { getFieldProps, touched, errors, handleSubmit } = useFormik({
  const { getFieldProps, touched, errors } = useFormik({
    initialValues: {
      emsMemberAccountId: ''
    },
    validationSchema: createValidationSchema(getInputs()),
    onSubmit: () => {
      //  required empty onSubmit
    },
    validateOnChange: false,
    enableReinitialize: true
  });

  const getControlProps = (name: string, type: InputType) => {
    return {
      ...createFormControlProps({
        name,
        type,
        touched,
        errors,
        codebooks: [],
        module: AppModule.EAI,
        entityRoot: I18N_COMPONENT_ROOT
      }),
      ...getFieldProps(name)
    };
  };

  const header: ReactElement = (
    <Stack direction='row' justifyContent='flex-start' spacing={GRID_SPACING}>
      <Grid item xs={6}>
        <FormControl
          {...getControlProps('searchString', InputType.TEXT_FIELD)}
          onChange={onSearchChange}
        />
      </Grid>
      <Grid item xs={3}>
        <Filter
          categories={filterStructure}
          filterState={filterState}
          onFilterSubmit={handleFilterChange}
        />
      </Grid>
      <ContextActionGuard
        module={AppModule.EAI}
        actionCode={CA_EAI_EDIT_DELIVERY_POINT_GDS}
        resourceEntity={EAIEntityTypeCode.DELIVERY_POINT}
        entityIdentificator={deliveryPointDetail?.id}
      >
        <Grid item xs={3}>
          <Button
            key={'create'}
            variant='contained'
            startIcon={<IconPlus></IconPlus>}
            disabled={operationInProgress}
            onClick={() => {
              setIsCreateGDSDialogOpen(true);
            }}
            title={t(`${I18N_COMPONENT_ROOT}.create`) ?? ''}
          >
            {t(`${I18N_COMPONENT_ROOT}.create`)}
          </Button>
        </Grid>
      </ContextActionGuard>
    </Stack>
  );

  return (
    <>
      <ContextActionGuard
        module={AppModule.EAI}
        actionCode={CA_EAI_LIST_DELIVERY_POINT_GDS}
        resourceEntity={EAIEntityTypeCode.DELIVERY_POINT}
        entityIdentificator={deliveryPointDetail?.id}
      >
        <DenseTable
          module={AppModule.EAI}
          i18nTableRoot={I18N_COMPONENT_ROOT}
          title={header}
          data={tableData}
          sorting={{
            sortingHeader: headCells,
            handleRequestSort: handleRequestSort
          }}
        />
      </ContextActionGuard>
      {isCreateGDSDialogOpen && deliveryPointDetail && (
        <CreateGDSDialog
          open={isCreateGDSDialogOpen}
          onClose={(reload) => {
            if (reload && deliveryPointDetail) {
              clearFilterAndSearchAndDispatchGetGDSList();
            }
            setIsCreateGDSDialogOpen(false);
          }}
          deliveryPointId={deliveryPointDetail.id}
        />
      )}
      {confirmationDialogParameter && (
        <ConfirmationDialog
          open={true}
          onSubmit={removeGDSFromDeliveryPoint}
          onClose={() => setConfirmationDialogParameter(undefined)}
          title={`${I18N_MODAL_ROOT}.title`}
          description={`${I18N_MODAL_ROOT}.description`}
          submitButtonTitle={`${I18N_MODAL_ROOT}.submitButtonTitle`}
        />
      )}
    </>
  );
};

export default GDSList;
